/**
 * useSeoMeta.js — v78.32
 *
 * Single source of truth for per-route `<head>` SEO tags.
 *
 * The Adukoo SPA was shipping the homepage's `<link rel=canonical>`,
 * `<meta description>`, `og:*`, and `twitter:*` tags on every route —
 * so Google consolidated ranking signals to `/` and inner pages never
 * earned their own rank. This hook patches the head on every route
 * mount so Googlebot (which executes JS) re-reads the right tags after
 * hydration, and any social crawler that runs through the prerender
 * sees per-page metadata too.
 *
 * Usage
 * -----
 * ```
 * useSeoMeta({
 *   title:       "Ford Figo diesel — Mumbai — Adukoo",
 *   description: "2018 Ford Figo diesel, 38,000 km, single owner...",
 *   canonical:   "https://adukoo.com/listing/abc123",
 *   image:       "https://adukoo.com/api/img?u=...",
 *   type:        "product",   // og:type (defaults to "website")
 *   jsonLd:      { "@context": "https://schema.org", "@type": "Product", ... },
 *   robots:      "index, follow",
 * });
 * ```
 *
 * All fields are optional. The hook is idempotent — re-running it
 * patches existing tags in place rather than appending duplicates. It
 * also restores the homepage defaults on unmount so back-navigation
 * never leaks a stale `canonical`.
 */

import { useEffect } from "react";

const HOMEPAGE_DEFAULTS = {
    title:       "Adukoo — Worldwide marketplace",
    description: "List Ads globally, open Digital Stores, reach buyers across 180+ countries. Adukoo — Worldwide marketplace.",
    canonical:   "https://adukoo.com/",
    image:       "https://adukoo.com/adukoo-og.jpg?v=tgk8iz",
    type:        "website",
    robots:      "index, follow, max-image-preview:large",
};

const JSON_LD_ID = "adukoo-seo-jsonld";

function _upsertMeta(selector, attrName, attrValue, content) {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
    return el;
}

function _upsertLink(rel, href) {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
    return el;
}

function _setJsonLd(payload) {
    let el = document.getElementById(JSON_LD_ID);
    if (!payload) {
        if (el) el.remove();
        return;
    }
    if (!el) {
        el = document.createElement("script");
        el.id = JSON_LD_ID;
        el.type = "application/ld+json";
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(payload);
}

export function useSeoMeta(opts) {
    // Stringify the JSON-LD up front so it can be used as a stable
    // dependency (eslint flags inline `JSON.stringify(...)` in dep arrays).
    const jsonLdKey = JSON.stringify(opts?.jsonLd || null);
    // `opts === null` or `opts === false` → caller is yielding control
    // to another component in the tree (e.g. Home defers to CountryLanding
    // when it's mounted inside one). Skip ALL DOM mutation in that case.
    const skip = opts === null || opts === false;
    useEffect(() => {
        if (skip) return undefined;
        if (typeof document === "undefined") return undefined;

        const cfg = { ...HOMEPAGE_DEFAULTS, ...(opts || {}) };
        const prev = {
            title: document.title,
            jsonLd: document.getElementById(JSON_LD_ID)?.textContent || null,
        };

        if (cfg.title) document.title = cfg.title;
        if (cfg.description) _upsertMeta('meta[name="description"]', "name", "description", cfg.description);
        if (cfg.robots) _upsertMeta('meta[name="robots"]', "name", "robots", cfg.robots);
        if (cfg.canonical) _upsertLink("canonical", cfg.canonical);

        _upsertMeta('meta[property="og:title"]',       "property", "og:title",       cfg.title || HOMEPAGE_DEFAULTS.title);
        _upsertMeta('meta[property="og:description"]', "property", "og:description", cfg.description || HOMEPAGE_DEFAULTS.description);
        if (cfg.canonical) _upsertMeta('meta[property="og:url"]', "property", "og:url", cfg.canonical);
        _upsertMeta('meta[property="og:image"]',       "property", "og:image",       cfg.image || HOMEPAGE_DEFAULTS.image);
        _upsertMeta('meta[property="og:image:secure_url"]', "property", "og:image:secure_url", cfg.image || HOMEPAGE_DEFAULTS.image);
        if (cfg.type) _upsertMeta('meta[property="og:type"]', "property", "og:type", cfg.type);

        _upsertMeta('meta[name="twitter:title"]',       "name", "twitter:title",       cfg.title || HOMEPAGE_DEFAULTS.title);
        _upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", cfg.description || HOMEPAGE_DEFAULTS.description);
        _upsertMeta('meta[name="twitter:image"]',       "name", "twitter:image",       cfg.image || HOMEPAGE_DEFAULTS.image);

        if (cfg.jsonLd) _setJsonLd(cfg.jsonLd);

        return () => {
            // Restore homepage defaults so back-navigation doesn't leak
            // a stale `canonical`. (Browser title is restored by the
            // next route's useSeoMeta call — and we keep `prev.title`
            // around just in case there isn't one.)
            if (cfg.canonical && cfg.canonical !== HOMEPAGE_DEFAULTS.canonical) {
                _upsertLink("canonical", HOMEPAGE_DEFAULTS.canonical);
                _upsertMeta('meta[property="og:url"]', "property", "og:url", HOMEPAGE_DEFAULTS.canonical);
            }
            if (prev.title && document.title !== prev.title) {
                // no-op: next route will replace this
            }
            if (cfg.jsonLd) {
                if (prev.jsonLd) {
                    const el = document.getElementById(JSON_LD_ID);
                    if (el) el.textContent = prev.jsonLd;
                } else {
                    _setJsonLd(null);
                }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        skip,
        opts?.title,
        opts?.description,
        opts?.canonical,
        opts?.image,
        opts?.type,
        opts?.robots,
        jsonLdKey,
    ]);
}

export const SEO_BASE_URL = "https://adukoo.com";
